import { ExpeditionLocality, LocalityEntryModeEnum } from '@app/pages/expedition/create/types.ts';
import { combineStops, dayKey, intersectTimeslots, locationKey, stopName } from '@app/pages/expedition/create/mergeRules.ts';

/**
 * Folding one stop into another on the user's say-so.
 *
 * The automatic merge ({@link mergeRepeatedStops}) leaves the same place apart when the shipments
 * need it on different days. Only the user knows whether one visit can serve both, so this is the
 * manual counterpart: the user picks the stop to keep, and the other one is folded into it.
 *
 * Unlike the automatic merge nothing here is refused. A fold that puts an unloading before its
 * loading still happens, and the route checks report it like any other move the user makes.
 */

/** Another stop at the same place that the current one could be folded into. */
export interface StopMergeCandidate {
  index: number;
  name: string;
  /** The days the stop is wanted on, as YYYY-MM-DD, in the order they are set. */
  days: string[];
  orderCount: number;
  /** Whether the two stops are now wanted at a time that suits both, i.e. the date was moved to match. */
  overlaps: boolean;
}

const hasDays = (loc: ExpeditionLocality): boolean => (loc.timeslots?.dayWithInterval?.length ?? 0) > 0;

/**
 * Stops at the same place as `locations[index]`, by the same rule the automatic merge uses.
 *
 * A stop with no days yet never counts as overlapping: it has not been given a date to match.
 */
export const sameLocationCandidates = (locations: ExpeditionLocality[], index: number): StopMergeCandidate[] => {
  const current = locations[index];
  const key = current ? locationKey(current) : null;
  if (!key) return [];

  return locations
    .map((loc, candidateIndex) => ({ loc, candidateIndex }))
    .filter(({ loc, candidateIndex }) => candidateIndex !== index && locationKey(loc) === key)
    .map(({ loc, candidateIndex }) => ({
      index: candidateIndex,
      name: stopName(loc),
      days: (loc.timeslots?.dayWithInterval ?? []).map(dayKey),
      orderCount: loc.orders?.length ?? 0,
      overlaps: hasDays(current) && hasDays(loc) && intersectTimeslots(current.timeslots, loc.timeslots) !== null,
    }));
};

/**
 * Folds `locations[sourceIndex]` into `locations[targetIndex]` and drops the source stop.
 *
 * The target is the stop the user chose to keep, so it keeps its date, its position in the route and
 * its contact details; the source only fills in what the target left blank. Orders loaded and
 * unloaded at the source move to the target with their ids unchanged, so every assignment still
 * points at the same order.
 */
export const mergeStopInto = (locations: ExpeditionLocality[], sourceIndex: number, targetIndex: number): ExpeditionLocality[] => {
  const source = locations[sourceIndex];
  const target = locations[targetIndex];
  if (!source || !target || sourceIndex === targetIndex) return locations;

  // Contact conflicts are not collected: the target wins them, so there is nothing left to report.
  const combined = combineStops(target, source, target.timeslots, []);
  const isManual = target.localityMode === LocalityEntryModeEnum.MANUALLY;

  const hasContact = (person: ExpeditionLocality['selectedPerson']) => Boolean(person?.contactName || person?.contactEmail);
  const mainContactEmail = target.manualAddress?.mainContactEmail || source.manualAddress?.mainContactEmail;

  const merged: ExpeditionLocality = {
    ...combined,
    selectedPerson: isManual ? target.selectedPerson : hasContact(target.selectedPerson) ? target.selectedPerson : source.selectedPerson,
    manualAddress: isManual
      ? {
          ...combined.manualAddress,
          mainContactPerson: target.manualAddress?.mainContactPerson || source.manualAddress?.mainContactPerson,
          mainContactPhone: target.manualAddress?.mainContactPhone || source.manualAddress?.mainContactPhone,
          mainContactEmail,
          notifySelectedPerson: Boolean(mainContactEmail) && Boolean(target.manualAddress?.notifySelectedPerson || source.manualAddress?.notifySelectedPerson),
        }
      : combined.manualAddress,
  };

  return locations.map((loc, index) => (index === targetIndex ? merged : loc)).filter((_, index) => index !== sourceIndex);
};
